import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Observable, forkJoin, from, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { ControlPoint } from 'src/app/bo/controlPoint';
import { DonneeAnalyseSiteDto } from 'src/app/bo/donneeAnalyse';
import { OrthoPhotoDto } from 'src/app/bo/orthophoto';
import { PolygonType } from 'src/app/bo/polygon/tirsPolygone';
import { ReferentielDonneeAnalyse } from 'src/app/bo/referentielDonneeAnalyse';
import { Site } from 'src/app/bo/site';
import { ZoneOfInterestDto } from 'src/app/bo/zoneOfInterest';
import { HttpService } from 'src/app/services/http.service';
import { KpiService } from 'src/app/services/kpi.service';
import { OrthophotoMapService } from 'src/app/services/map.services';
import { ZoneOfInterestService } from 'src/app/services/zone-of-interest.service';
import { Equipment } from 'src/app/bo/equipment';
import { SitePolygoneDto } from 'src/app/bo/site/sitePolygone';
import { DxfServiceService } from 'src/app/services/dxf-service.service';
import { ShiftEnum } from 'src/app/bo/shifts/shift';
import { DEFAULT_POLYGON_FILL, EQUIPMENT_SITE_ID } from './site-map.constants';
import {
  HoleFeatureFilter,
  HoleLegends,
  HolesPanelTab,
  HoleStatusFilter,
  KpiDisplayValue,
  KpiScale,
  SiteMapBlast,
  DrawnGeometry,
  SiteMapDrawShape,
  SiteMapPanel,
  SiteMapPopup,
  SiteMapTool,
  SiteMapViewMode,
  SiteProjection,
  ZoneLegendEntry,
} from './site-map.types';
import { EMPTY_HOLE_LEGENDS } from './utils/hole-appearance';
import {
  buildZonePalette,
  computeKpiScale,
  kpiColor,
  parseConfiguredKpiIds,
  parseKpiValue,
} from './utils/kpi-scale';
import {
  PREFERENCE_KEYS,
  loadBooleanPreference,
  loadPolygonTypesPreference,
  saveBooleanPreference,
  savePolygonTypesPreference,
} from './utils/map-preferences';
import { ensureProjection, normalizeEpsgCode } from './utils/projection';

interface SiteMapData {
  controlPoints: ControlPoint[];
  activeKpis: ReferentielDonneeAnalyse[];
  kpiValues: DonneeAnalyseSiteDto[];
  zonesOfInterest: ZoneOfInterestDto[];
  orthophotos: OrthoPhotoDto[];
}

@Injectable()
export class SiteMapStore {
  private readonly http = inject(HttpService);
  private readonly kpiService = inject(KpiService);
  private readonly zoneOfInterestService = inject(ZoneOfInterestService);
  private readonly orthophotoService = inject(OrthophotoMapService);
  private readonly dxfService = inject(DxfServiceService);

  readonly site = signal<Site | null>(null);
  readonly blasts = signal<SiteMapBlast[]>([]);
  readonly visibleBlastIds = signal<number[]>([]);

  readonly siteProjection = signal<SiteProjection | null>(null);
  readonly controlPoints = signal<ControlPoint[]>([]);
  readonly zonesOfInterest = signal<ZoneOfInterestDto[]>([]);
  readonly orthophotos = signal<OrthoPhotoDto[]>([]);
  readonly activeKpis = signal<ReferentielDonneeAnalyse[]>([]);
  readonly kpiValues = signal<DonneeAnalyseSiteDto[]>([]);

  readonly viewMode = signal<SiteMapViewMode>(loadBooleanPreference(PREFERENCE_KEYS.satellite, false) ? 'map2d' : 'plan2d');
  readonly orthophotosVisible = signal(loadBooleanPreference(PREFERENCE_KEYS.orthophotos, true));
  readonly controlPointsVisible = signal(loadBooleanPreference(PREFERENCE_KEYS.controlPoints, true));
  readonly zonesOfInterestVisible = signal(loadBooleanPreference(PREFERENCE_KEYS.zonesOfInterest, true));
  readonly polygonTypes = signal<PolygonType[]>(loadPolygonTypesPreference());
  readonly holesEnabled = signal(true);

  readonly activePanel = signal<SiteMapPanel | null>(null);
  readonly activeTool = signal<SiteMapTool | null>(null);
  /** Forme choisie pour les outils de création (polygone ou cercle) ; null tant que l'utilisateur n'a pas choisi. */
  readonly drawShape = signal<SiteMapDrawShape | null>(null);
  readonly drawnGeometry = signal<DrawnGeometry | null>(null);
  /** Tir dont la popup était ouverte à l'activation de l'outil : il est présélectionné pour le polygone créé. */
  readonly toolBlastId = signal<number | null>(null);
  readonly polygonCreated = signal<{ blastId: number; type: PolygonType } | null>(null);
  readonly holesPanelTab = signal<HolesPanelTab>('state');
  readonly holeStatusFilter = signal<HoleStatusFilter>('actual');
  readonly holeFeatureFilter = signal<HoleFeatureFilter>('all');
  readonly selectedKpiId = signal<number | null>(null);
  readonly useZoneColors = signal(false);

  readonly zoomedIn = signal(false);
  readonly holeLegends = signal<HoleLegends>(EMPTY_HOLE_LEGENDS);
  readonly popup = signal<SiteMapPopup | null>(null);

  readonly dxfLayers = signal<SitePolygoneDto[]>([]);
  readonly dxfReloadTick = signal(0);
  readonly equipments = signal<Equipment[]>([]);
  readonly equipmentDate = signal<Date | null>(null);
  readonly equipmentShift = signal<ShiftEnum | null>(null);

  readonly siteId = computed(() => this.site()?.siteId ?? null);
  readonly equipmentEnabled = computed(() => this.siteId() === EQUIPMENT_SITE_ID);
  readonly holesShown = computed(() => this.holesEnabled() && this.zoomedIn());
  readonly blastsById = computed(() => new Map(this.blasts().map(blast => [blast.tirId, blast])));
  readonly configuredKpiIds = computed(() => parseConfiguredKpiIds(this.site()?.configurationKPI));

  readonly selectedKpi = computed(() =>
    this.activeKpis().find(kpi => kpi.referentielDonneeAnalyseId === this.selectedKpiId()) ?? null);

  readonly selectedKpiValues = computed(() => {
    const kpi = this.selectedKpi();
    const valuesByBlast = new Map<number, DonneeAnalyseSiteDto>();
    if (!kpi) return valuesByBlast;
    for (const value of this.kpiValues()) {
      if (value.referentielDonneeAnalyseId === kpi.referentielDonneeAnalyseId) valuesByBlast.set(value.tirId, value);
    }
    return valuesByBlast;
  });

  readonly kpiScale = computed<KpiScale | null>(() => {
    const kpi = this.selectedKpi();
    return kpi ? computeKpiScale(kpi, [...this.selectedKpiValues().values()]) : null;
  });

  readonly zonePalette = computed(() => buildZonePalette(
    this.blasts().map(blast => blast.zoneId).filter((zoneId): zoneId is number => !!zoneId && zoneId > 0)));

  readonly zoneLegend = computed<ZoneLegendEntry[]>(() => {
    const palette = this.zonePalette();
    const names = new Map<number, string>();
    for (const blast of this.blasts()) {
      if (blast.zoneId && !names.has(blast.zoneId)) names.set(blast.zoneId, blast.nomZone || `Zone ${blast.zoneId}`);
    }
    return [...palette].map(([zoneId, color]) => ({ zoneId, name: names.get(zoneId) ?? `Zone ${zoneId}`, color }));
  });

  readonly polygonColorOf = computed<(blastId: number) => string>(() => {
    if (this.useZoneColors()) {
      const palette = this.zonePalette();
      const blastsById = this.blastsById();
      return blastId => palette.get(blastsById.get(blastId)?.zoneId) ?? DEFAULT_POLYGON_FILL;
    }
    const scale = this.kpiScale();
    if (!scale) return () => DEFAULT_POLYGON_FILL;
    const values = this.selectedKpiValues();
    return blastId => kpiColor(parseKpiValue(values.get(blastId)?.valeur), scale);
  });

  constructor() {
    toObservable(this.siteId).pipe(
      distinctUntilChanged(),
      switchMap(siteId => (siteId === null ? of(null) : this.loadSiteData(siteId).pipe(startWith(null)))),
      takeUntilDestroyed(),
    ).subscribe(data => this.applySiteData(data));

    // Un tracé commencé sur un site ne doit pas se poursuivre ni se sauvegarder sur le suivant.
    toObservable(this.siteId).pipe(distinctUntilChanged(), takeUntilDestroyed()).subscribe(() => this.closeTool());

    toObservable(this.site).pipe(
      map(site => normalizeEpsgCode(site?.epsg)),
      distinctUntilChanged(),
      switchMap(code => from(ensureProjection(this.http, code)).pipe(map(registered => ({ code, registered })))),
      takeUntilDestroyed(),
    ).subscribe(projection => this.siteProjection.set(projection));
  }

  setViewMode(mode: SiteMapViewMode): void {
    this.viewMode.set(mode);
    // La 3D n'est pas un fond 2D : on garde le dernier choix Plan / Map.
    if (mode !== '3d') saveBooleanPreference(PREFERENCE_KEYS.satellite, mode === 'map2d');
  }

  toggleOrthophotos(): void {
    this.orthophotosVisible.update(visible => !visible);
    saveBooleanPreference(PREFERENCE_KEYS.orthophotos, this.orthophotosVisible());
  }

  toggleControlPoints(): void {
    this.controlPointsVisible.update(visible => !visible);
    saveBooleanPreference(PREFERENCE_KEYS.controlPoints, this.controlPointsVisible());
  }

  toggleZonesOfInterest(): void {
    this.zonesOfInterestVisible.update(visible => !visible);
    saveBooleanPreference(PREFERENCE_KEYS.zonesOfInterest, this.zonesOfInterestVisible());
  }

  toggleHoles(): void {
    this.holesEnabled.update(enabled => !enabled);
  }

  togglePolygonType(type: PolygonType): void {
    this.polygonTypes.update(types => (types.includes(type) ? types.filter(t => t !== type) : [...types, type]));
    savePolygonTypesPreference(this.polygonTypes());
  }

  togglePanel(panel: SiteMapPanel): void {
    this.activePanel.update(current => (current === panel ? null : panel));
  }

  /** Un seul outil de dessin à la fois ; recliquer sur l'outil actif le désactive. */
  toggleTool(tool: SiteMapTool): void {
    const next = this.activeTool() === tool ? null : tool;
    this.drawShape.set(null);
    this.drawnGeometry.set(null);
    if (next) {
      const visibleBlastIds = this.visibleBlastIds();
      this.toolBlastId.set(this.popup()?.blastId ?? (visibleBlastIds.length === 1 ? visibleBlastIds[0] : null));
      this.popup.set(null);
    }
    this.activeTool.set(next);
  }

  closeTool(): void {
    this.drawShape.set(null);
    this.drawnGeometry.set(null);
    this.activeTool.set(null);
  }

  /** Signale à la carte qu'un polygone vient d'être créé : elle recharge le tir et le type devient visible. */
  notifyPolygonCreated(blastId: number, type: PolygonType): void {
    if (!this.polygonTypes().includes(type)) this.togglePolygonType(type);
    this.polygonCreated.set({ blastId, type });
  }

  /** Recharge les orthophotos du site, par exemple après l'ajout d'un fichier. */
  reloadOrthophotos(): void {
    const siteId = this.siteId();
    if (siteId === null) return;
    from(this.orthophotoService.getOrthophotoMaps(siteId)).pipe(catchError(() => of(null))).subscribe(list => {
      if (list && this.siteId() === siteId) this.orthophotos.set(list);
    });
  }

  /** Applique un nouvel ordre d'empilement aux orthophotos (le premier est rendu au-dessus) et le mémorise pour le site. */
  reorderOrthophotos(ordered: OrthoPhotoDto[]): Observable<{ statusCode?: number; message?: string } | null> {
    const siteId = this.siteId();
    this.orthophotos.set(ordered.map((item, index) => {
      const updated = Object.assign(new OrthoPhotoDto(), item);
      updated.zOrder = index + 1;
      return updated;
    }));
    if (siteId === null || ordered.length < 2) return of(null);

    return this.http.post<object[], { statusCode?: number; message?: string }>(
      `sites/${siteId}/map/order`,
      this.orthophotos().map(item => ({ name: item.mapName, ordre: item.zOrder, guidProcess: item.tileSetId, isActif: item.isVisible })),
    );
  }

  deleteOrthophoto(orthophoto: OrthoPhotoDto): Observable<{ statusCode?: number; message?: string }> {
    return this.http.put<object, { statusCode?: number; message?: string }>('GeoData/DeleteLayer', {
      siteId: this.siteId(),
      guidProcess: orthophoto.tileSetId || '',
      name: orthophoto.mapName,
      fileType: 'ORT',
    });
  }

  removeOrthophoto(orthophotoId: number): void {
    this.orthophotos.update(list => list.filter(item => item.id !== orthophotoId));
  }

  /** Demande à la carte de recharger les couches DXF du site, par exemple après l'ajout d'un fichier. */
  reloadDxfLayers(): void {
    this.dxfReloadTick.update(tick => tick + 1);
  }

  /** Affiche ou masque une orthophoto et mémorise le choix pour le site ; le changement est annulé si l'appel échoue. */
  toggleOrthophoto(orthophoto: OrthoPhotoDto): void {
    const siteId = this.siteId();
    if (siteId === null) return;
    const visible = !orthophoto.isVisible;
    this.setOrthophotoVisibility(orthophoto.id, visible);

    const revert = () => this.setOrthophotoVisibility(orthophoto.id, !visible);
    this.http.post<object, { statusCode?: number }>(
      `sites/${siteId}/map/${orthophoto.tileSetId}/visibility?isVisible=${visible}`, {},
    ).subscribe({
      next: result => { if ((result?.statusCode ?? 200) > 299) revert(); },
      error: revert,
    });
  }

  /** Affiche ou masque une couche DXF et mémorise le choix pour le site ; le changement est annulé si l'appel échoue. */
  toggleDxfLayer(polygone: SitePolygoneDto): void {
    const siteId = this.siteId();
    if (siteId === null) return;
    const visible = !polygone.isVisible;
    this.setDxfVisibility(polygone.id, visible);
    this.dxfService.setSitePolygoneVisibility(siteId, polygone.id, visible)
      .catch(() => this.setDxfVisibility(polygone.id, !visible));
  }

  private setOrthophotoVisibility(orthophotoId: number, visible: boolean): void {
    this.orthophotos.update(list => list.map(item => {
      if (item.id !== orthophotoId) return item;
      const updated = Object.assign(new OrthoPhotoDto(), item);
      updated.isVisible = visible;
      return updated;
    }));
  }

  private setDxfVisibility(polygoneId: number, visible: boolean): void {
    this.dxfLayers.update(list => list.map(item => (item.id === polygoneId ? { ...item, isVisible: visible } : item)));
  }

  reloadZonesOfInterest(): void {
    const siteId = this.siteId();
    if (siteId === null) return;
    this.zoneOfInterestService.getAll(siteId).pipe(catchError(() => of(null))).subscribe(zones => {
      if (zones && this.siteId() === siteId) this.zonesOfInterest.set(zones);
    });
  }

  /** Les filtres de statut et de caractéristique sont exclusifs : en choisir un réinitialise l'autre. */
  setHoleStatusFilter(filter: HoleStatusFilter): void {
    this.holeStatusFilter.set(filter);
    this.holeFeatureFilter.set('all');
  }

  setHoleFeatureFilter(filter: HoleFeatureFilter): void {
    this.holeFeatureFilter.set(filter);
    this.holeStatusFilter.set('actual');
  }

  selectKpi(kpiId: number | null): void {
    this.selectedKpiId.set(kpiId);
  }

  setZoneColors(enabled: boolean): void {
    this.useZoneColors.set(enabled);
  }

  openPopup(popup: SiteMapPopup): void {
    this.popup.set(popup);
  }

  closePopup(): void {
    this.popup.set(null);
  }

  /** Valeur du KPI sélectionné pour un tir, telle qu'affichée dans sa popup (masquée en coloration par zones). */
  blastSelectedKpi(blastId: number): KpiDisplayValue | null {
    const kpi = this.selectedKpi();
    if (!kpi || this.useZoneColors()) return null;
    const value = this.selectedKpiValues().get(blastId);
    return value?.valeur ? { name: kpi.nom, value: value.valeur, unit: value.unite ?? '' } : null;
  }

  /** Valeurs, pour un tir, des KPI de référence configurés sur le site (4 au plus). */
  blastConfiguredKpis(blastId: number): KpiDisplayValue[] {
    const values = this.kpiValues();
    const kpis = this.activeKpis();
    return this.configuredKpiIds().slice(0, 4).flatMap(kpiId => {
      if (kpiId === null) return [];
      const value = values.find(v => v.referentielDonneeAnalyseId === kpiId && v.tirId === blastId);
      const kpi = kpis.find(k => k.referentielDonneeAnalyseId === kpiId);
      return value?.valeur && kpi ? [{ name: kpi.nom, value: value.valeur, unit: value.unite ?? '' }] : [];
    });
  }

  private loadSiteData(siteId: number): Observable<SiteMapData> {
    return forkJoin({
      controlPoints: this.http.get<ControlPoint[]>(`Sites/${siteId}/ControlPoints?transformCoord=false`).pipe(catchError(() => of([]))),
      activeKpis: this.kpiService.getSiteActiveAnalysisEntry(siteId).pipe(catchError(() => of([]))),
      kpiValues: this.http.get<DonneeAnalyseSiteDto[]>(`Sites/${siteId}/AnalysisDataValues`).pipe(catchError(() => of([]))),
      zonesOfInterest: this.zoneOfInterestService.getAll(siteId).pipe(catchError(() => of([]))),
      orthophotos: from(this.orthophotoService.getOrthophotoMaps(siteId)).pipe(catchError(() => of([]))),
    });
  }

  /** Remplace les données du site ; null vide immédiatement celles du site précédent pendant le chargement. */
  private applySiteData(data: SiteMapData | null): void {
    this.controlPoints.set(data?.controlPoints ?? []);
    this.activeKpis.set(data?.activeKpis ?? []);
    this.kpiValues.set(data?.kpiValues ?? []);
    this.zonesOfInterest.set(data?.zonesOfInterest ?? []);
    this.orthophotos.set(data?.orthophotos ?? []);

    const defaultKpiId = this.configuredKpiIds()[0] ?? null;
    const hasDefaultKpi = !!data?.activeKpis.some(kpi => kpi.referentielDonneeAnalyseId === defaultKpiId);
    this.selectedKpiId.set(hasDefaultKpi ? defaultKpiId : null);

    if (!data) {
      this.popup.set(null);
      this.holeLegends.set(EMPTY_HOLE_LEGENDS);
    }
  }
}
